import { useCallback, useEffect } from 'react';
import type { WidthKey } from './constants';
import { useSettingsPanelContext } from './context';
import { GlobalSettingsPanel } from './helpers';
import type { SettingsPanelConfig } from './SETTINGS-PANEL-CONFIG';
import { useSettingsPanelStore } from './store';

/**
 * A hook that returns the current width of the current panel (within a SettingsPanelPortal).
 *
 * This hook uses the panel context and panel store to retrieve the current width
 * of the specific panel identified by its ID.
 *
 * @returns {WidthKey} The current width key of the panel
 */
export function useCurrentSettingsPanelWidth(): WidthKey {
  const { id } = useSettingsPanelContext();
  return useSettingsPanelStore((state) => {
    return state.currentPanelWidths[id];
  });
}

/**
 * A hook that returns the mount target id of the current panel (within a SettingsPanelPortal).
 *
 * @returns {string} The mount target id of the panel
 */
export function useCurrentSettingsPanelMountTargetId(): string {
  const { mountTargetId } = useSettingsPanelContext();
  return mountTargetId;
}

/**
 * A hook that returns a function to update the width of the current panel (within a SettingsPanelPortal).
 *
 * @returns A function that accepts a WidthKey parameter to set the new width of the current panel.
 *
 * @example
 * ```tsx
 * const updateWidth = useUpdateCurrentSettingsPanelWidth();
 * updateWidth('lg'); // Updates the current panel's width to large
 * ```
 */
export function useUpdateCurrentSettingsPanelWidth(): (width: WidthKey) => void {
  const { id } = useSettingsPanelContext();
  const updatePanelWidth = useSettingsPanelStore((state) => state.updatePanelWidth);

  return (width: WidthKey) => {
    updatePanelWidth(id, width);
  };
}

/**
 * A hook that returns the width of a specific panel identified by its ID.
 * It can be used to retrieve the width of any panel that's registered in the panel store.
 *
 * @param id The ID of the panel
 *
 * @returns {WidthKey} The current width key of the panel
 */
export const useGetSettingsPanelWidth = (id: SettingsPanelConfig) => {
  return useSettingsPanelStore((state) => {
    return state.currentPanelWidths[id];
  });
};

/**
 * A hook that returns a function to update the width of a specific panel identified by its ID.
 *
 * @param id The ID of the panel
 *
 * @returns A function that accepts a WidthKey parameter to set the new width of the panel.
 *
 * @example
 * ```tsx
 * const updateWidth = useUpdateSettingsPanelWidth('my-panel-id');
 * updateWidth('lg'); // Updates the panel's width to large
 * ```
 */
export const useUpdateSettingsPanelWidth = (id: SettingsPanelConfig) => {
  const updatePanelWidth = useSettingsPanelStore((state) => state.updatePanelWidth);
  return (width: WidthKey) => {
    updatePanelWidth(id, width);
  };
};

/**
 * Custom hook to manage the state and actions for a specific global settings panel.
 *
 * This hook subscribes to the `useSettingsPanelStore` to get the current open state
 * of the panel identified by the provided `id`. It also provides memoized
 * callback functions to open and close the panel using the `GlobalSettingsPanel` utility.
 *
 * @param id - The configuration ID (`SettingsPanelConfig`) of the global panel to manage.
 * @returns An object containing:
 *   - `isOpen`: A boolean indicating whether the specified panel is currently open.
 *   - `close`: A memoized function to close the specified panel.
 *   - `open`: A memoized function to open the specified panel.
 *
 * @example
 * ```tsx
 * import { useGlobalSettingsPanel } from './hooks';
 *
 * const MyComponent = () => {
 *   const { isOpen, open, close } = useGlobalSettingsPanel('account-settings');
 *
 *   return (
 *     <div>
 *       <button onClick={open}>Open Settings</button>
 *       {isOpen && (
 *         <div>
 *           Panel Content
 *           <button onClick={close}>Close</button>
 *         </div>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 */
export const useGlobalSettingsPanel = (id: SettingsPanelConfig) => {
  const isOpen = !!useSettingsPanelStore((state) => state.openGlobalPanels[id]);

  const close = useCallback(() => {
    GlobalSettingsPanel.close(id);
  }, [id]);

  const open = useCallback(() => {
    GlobalSettingsPanel.open(id);
  }, [id]);

  return { close, isOpen, open };
};

/**
 * Custom hook to manage the force tray functionality for settings panels.
 *
 * This hook provides access to the current force tray state and functions to control it.
 * When force tray is enabled, all panels will be forced to open in the tray regardless
 * of available space. The force tray mode is automatically disabled when all panels are closed.
 *
 * @returns An object containing:
 *   - `forceTray`: A boolean indicating whether force tray mode is currently active.
 *   - `setForceTray`: A function to enable or disable force tray mode.
 *   - `enableForceTray`: A convenience function to enable force tray mode.
 *   - `disableForceTray`: A convenience function to disable force tray mode.
 *
 * @example
 * ```tsx
 * import { useSettingsForceTray } from './hooks';
 *
 * const MyComponent = () => {
 *   const { forceTray, enableForceTray, disableForceTray } = useSettingsForceTray();
 *
 *   return (
 *     <div>
 *       <p>Force Tray Mode: {forceTray ? 'Enabled' : 'Disabled'}</p>
 *       <button onClick={enableForceTray}>Enable Force Tray</button>
 *       <button onClick={disableForceTray}>Disable Force Tray</button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useSettingsForceTray = () => {
  const forceTray = useSettingsPanelStore((state) => state.forceTray);
  const setForceTray = useSettingsPanelStore((state) => state.setForceTray);

  const enableForceTray = useCallback(() => {
    setForceTray(true);
  }, [setForceTray]);

  const disableForceTray = useCallback(() => {
    setForceTray(false);
  }, [setForceTray]);

  return {
    forceTray,
    setForceTray,
    enableForceTray,
    disableForceTray,
  };
};

/**
 * A hook to manage the height offset for the settings panel layout.
 *
 * This hook allows you to subscribe to changes in the height offset,
 * which can be useful for adjusting the layout based on dynamic content
 * or other factors that affect the height of the panel.
 *
 * @param offset The height offset to apply.
 */
export function useSettingsHeightOffset(offset: number) {
  useEffect(() => {
    useSettingsPanelStore.setState((state) => ({
      heightOffset: state.heightOffset + offset,
    }));

    return () => {
      useSettingsPanelStore.setState((state) => ({
        heightOffset: state.heightOffset - offset,
      }));
    };
  }, [offset]);
}
